/**
 * Pure formatting helpers for the Upgrade to Pro (TG8) and Manage
 * Subscription (TG9) pages — extracted so they're testable without
 * Angular's TestBed/DI (this repo's Jest config can't parse a bare
 * `@angular/*` import in a spec at all, per membership-tier-utils.ts's
 * own docstring).
 */

/**
 * `$1,500/yr` from `priceCents`. Never hardcode the $1,500 figure per
 * spec.md FR-4.1 — always sourced live from `MembershipTierService.priceCents()`,
 * which itself comes from a live Stripe Price lookup, never a cached
 * constant. Returns a fallback when the price hasn't loaded (or the Hub's
 * own best-effort Price lookup failed and returned null) rather than
 * showing a broken or misleading number.
 */
export function formatAnnualPrice(cents: number | null): string {
  if (cents === null) return 'Price unavailable';
  const dollars = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
  return `${dollars}/yr`;
}

/** ADN's calendar is Central; complimentary grants end at midnight Central. */
const ACCESS_TIME_ZONE = 'America/Chicago';

/**
 * The last calendar day (Central) the subscription still grants access. The
 * period end is the instant access stops, so a midnight cutoff on Jan 1 means
 * the last day is Dec 31 — which is also how Stripe's receipt reads.
 */
function formatLastDayOfAccess(periodEnd: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: ACCESS_TIME_ZONE,
  }).format(new Date(periodEnd.getTime() - 1000));
}

/**
 * Status line for the Manage Subscription status card. `cancelAtPeriodEnd`
 * is `boolean | null` — `null` means "not yet known" (see
 * membership-tier.model.ts), distinct from an observed `false`. Collapsing
 * that to `false` would claim "renews" when the truth is "we don't know
 * yet," the same distinction the Hub's own `/subscriptions/status`
 * response preserves verbatim rather than defaulting.
 */
export function formatRenewalLine(renewalDate: Date | null, cancelAtPeriodEnd: boolean | null): string {
  if (!renewalDate) {
    return 'Renewal date unavailable';
  }
  const formatted = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(renewalDate);

  if (cancelAtPeriodEnd === true) return `Access through ${formatLastDayOfAccess(renewalDate)}`;
  if (cancelAtPeriodEnd === false) return `Renews on ${formatted}`;
  return `Next billing date ${formatted} — cancellation status pending`;
}
